import React, { createContext, useState, useContext, ReactNode } from 'react'
import ConfirmDialog from '../components/ConfirmDialog'

type ConfirmOptions = {
  title?: string
  description?: string
  confirmText?: string
  cancelText?: string
}

type ConfirmFn = (options: ConfirmOptions | string) => Promise<boolean>

const ConfirmContext = createContext<ConfirmFn | undefined>(undefined)

const ConfirmProvider = ({ children }: { children: ReactNode }) => {
  const [state, setState] = useState<{
    open: boolean
    options: ConfirmOptions
    resolve: (result: boolean) => void
  } | null>(null)

  const confirm: ConfirmFn = (options) => {
    const opts: ConfirmOptions =
      typeof options === 'string' ? { description: options } : options
    return new Promise<boolean>((resolve) => {
      setState({ open: true, options: opts, resolve })
    })
  }

  const handleClose = (result: boolean) => {
    state?.resolve(result)
    setState(null)
  }

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {state?.open && (
        <ConfirmDialog
          open={state.open}
          title={state.options.title}
          description={state.options.description}
          confirmText={state.options.confirmText}
          cancelText={state.options.cancelText}
          onClose={handleClose}
        />
      )}
    </ConfirmContext.Provider>
  )
}

const useConfirm = (): ConfirmFn => {
  const context = useContext(ConfirmContext)
  if (!context) {
    throw new Error('useConfirm must be used within ConfirmProvider')
  }
  return context
}

// Export all hooks and components
export { ConfirmProvider, useConfirm }